/**
 * Weight Freeze (v2) — number and currency formatters.
 * House style matches `disputes-v2/format.js`: plain functions, en-IN
 * locale grouping, no slab rounding on weights.
 */

/** Rounds to 1 decimal and lets JS's own number-to-string coercion drop trailing zeros. */
function roundTrim(value) {
  return Math.round(value * 10) / 10
}

/** Whole rupees, en-IN grouping. Minus sign goes before the ₹ symbol, e.g. '-₹500'. */
export function inr(n) {
  const v = Math.round(Number(n) || 0)
  const sign = v < 0 ? '-' : ''
  return sign + '₹' + Math.abs(v).toLocaleString('en-IN')
}

/** Lakh/crore short currency form. Trims trailing zeros: 1.0L renders as 1L. */
export function inrShort(n) {
  const v = Number(n) || 0
  const sign = v < 0 ? '-' : ''
  const abs = Math.abs(v)
  if (abs >= 1e7) return sign + '₹' + roundTrim(abs / 1e7) + 'Cr'
  if (abs >= 1e5) return sign + '₹' + roundTrim(abs / 1e5) + 'L'
  return inr(v)
}

/**
 * Whole-percent display. Deliberately renders any value strictly between 0
 * and 1 as 'Under 1%' rather than rounding it down to '0%' — a real sub-1%
 * spread is a genuine measurement, not "no data". Exactly 0 is '0%'.
 */
export function pct(n) {
  const v = Number(n) || 0
  if (v > 0 && v < 1) return 'Under 1%'
  return Math.round(v) + '%'
}

/** Like pct, but keeps one decimal below 1% so sub-1% values don't collapse into duplicate ticks. */
export function pctPrecise(n) {
  const v = Number(n) || 0
  if (v > 0 && v < 1) return v.toFixed(1) + '%'
  return Math.round(v) + '%'
}

/** Two decimals, always — this data bills in 0.75 kg steps, so it must never slab-round. */
export function kg(n) {
  return (Number(n) || 0).toFixed(2) + ' kg'
}

/** Whole number, en-IN grouping. */
export function int(n) {
  return Math.round(Number(n) || 0).toLocaleString('en-IN')
}

/** Generic (non-currency) compact number using Indian units: k / L / Cr. */
export function compact(n) {
  const v = Number(n) || 0
  const sign = v < 0 ? '-' : ''
  const abs = Math.abs(v)
  if (abs >= 1e7) return sign + roundTrim(abs / 1e7) + 'Cr'
  if (abs >= 1e5) return sign + roundTrim(abs / 1e5) + 'L'
  if (abs >= 1e3) return sign + roundTrim(abs / 1e3) + 'k'
  return sign + String(Math.round(abs))
}

/**
 * '30 × 24 × 12 cm' from `[l, b, h]` or `{l, b, h}`. Returns an em dash when
 * dims are absent or any single component is 0 — a caller decides whether
 * "absent" reads as prose like 'not recorded'; this formatter never guesses.
 */
export function dims(d) {
  if (!d) return '—'
  const [l, b, h] = Array.isArray(d) ? d : [d.l, d.b, d.h]
  if (!l || !b || !h) return '—'
  return `${l} × ${b} × ${h} cm`
}

/**
 * Whole grams, e.g. '165 g' — or an em dash when the packaging's own weight has not been recorded.
 *
 * The backend sends null for this until a merchant fills it in, and no packaging weighs nothing, so
 * "0 g" would state a measurement that was never taken. Matches `dims` directly above, which
 * already renders a missing triple as '—'.
 */
export function grams(g) {
  const n = Number(g)
  if (g == null || !Number.isFinite(n) || n <= 0) return '—'
  return Math.round(n) + ' g'
}

/**
 * en-IN date, 2-digit day / short month / numeric year — matches the sibling
 * Disputes module's `fmtDate`. Formatted with `timeZone: 'UTC'` so a UTC
 * midnight (this dataset's convention) renders as its own calendar date
 * regardless of the host machine's local offset, instead of drifting onto
 * the adjacent day.
 */
export function date(iso) {
  const d = iso instanceof Date ? iso : new Date(iso)
  return d.toLocaleDateString('en-IN', {day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC'})
}

/**
 * Indian-unit compact form for chart axes (k / L / Cr).
 *
 * This is deliberately an ALIAS of `compact`, not a second implementation.
 * The Section 0 brief justified a separate function on the grounds that
 * `compact` shows a decimal below 1000 — it does not; its final branch is a
 * bare `Math.round`. Shipping two identical bodies is the very drift Section 0
 * exists to prevent (the reconciliation found four bodies for `dims` and three
 * for `date` across six section plans).
 *
 * Analytics imports this name because a chart axis reads better as
 * `axisCompact`; if the two ever need to diverge, split them then and say why.
 */
export const axisCompact = compact
